import {Button, Heading, Text, type HeadingProps} from '@primer/react-brand'
import {createContext, Fragment, useContext} from 'react'

import {Flash} from './components/Flash'
import {Octocaptcha} from './components/Octocaptcha/Octocaptcha'
import type {useForm} from './hooks/useForm'

import styles from './Form.module.css'

export type FormProps = Pick<React.FormHTMLAttributes<HTMLFormElement>, 'children' | 'onSubmit'>

export const FormContext = createContext<ReturnType<typeof useForm> | undefined>(undefined)

const Root = (props: FormProps) => {
  return (
    <form noValidate onSubmit={props.onSubmit}>
      {props.children}
    </form>
  )
}

export type FormHeadingProps = Pick<HeadingProps, 'children' | 'size' | 'as'>

const FormHeading = (props: FormHeadingProps) => {
  return <Heading as="h4" {...props} />
}

export type FormSubmitProps = {
  children?: React.ReactNode
}

const FormSubmit = (props: FormSubmitProps) => {
  return (
    <Button variant="primary" type="submit">
      {props.children}
    </Button>
  )
}

const FormErrors = () => {
  const formContext = useContext(FormContext)

  return (
    <Flash hidden={formContext === undefined || Object.keys(formContext.errors).length === 0} variant="error">
      <Text as="p" weight="semibold" size="200">
        The following fields have errors:
      </Text>

      <Text as="p" size="200">
        {Object.keys(formContext?.errors ?? []).map((name, index, collection) => {
          const maybeFormField = formContext?.formFields[name]

          if (maybeFormField === undefined) {
            /**
             * It is very unlikely that we reach this point, but if we do, we silently
             * skips this error.
             */
            return null
          }

          const isLastElement = index === collection.length - 1
          const label = maybeFormField.label ?? maybeFormField.name

          return (
            <Fragment key={maybeFormField.name}>
              <a href={`#${maybeFormField.id}`} className={styles.FormErrorsItem}>
                {label}
              </a>

              {!isLastElement && ', '}
            </Fragment>
          )
        })}
      </Text>
    </Flash>
  )
}

const FormOctocaptcha = () => {
  const formContext = useContext(FormContext)

  return (
    <Octocaptcha
      onComplete={formContext?.octocaptcha.onOctocaptchaComplete}
      onLoadError={formContext?.octocaptcha.onOctocaptchaLoadError}
    />
  )
}

const Form = Object.assign(Root, {
  Errors: FormErrors,
  Heading: FormHeading,
  Octocaptcha: FormOctocaptcha,
  Submit: FormSubmit,
})

export {Form}

try{ FormContext.displayName ||= 'FormContext' } catch {}
try{ Root.displayName ||= 'Root' } catch {}
try{ FormHeading.displayName ||= 'FormHeading' } catch {}
try{ FormSubmit.displayName ||= 'FormSubmit' } catch {}
try{ FormErrors.displayName ||= 'FormErrors' } catch {}
try{ FormOctocaptcha.displayName ||= 'FormOctocaptcha' } catch {}
try{ Form.displayName ||= 'Form' } catch {}